import { get } from 'svelte/store';
import { isAuthenticated } from './auth-handler.js';
import { userStore } from '../stores/user-store.js';
import { ROUTE } from '../../constants/route-constants.js';
import type { RouteData } from '../routing/route-store.js';
import { logInfo, logWarning, logError } from '../logging/log-handler.js';

export interface RouteGuardResult {
    isAllowed: boolean;
    redirectTo?: string;
    reason?: string;
}

// Update the params type to match the route store type
interface RouteParams {
    id?: string;
    [key: string]: any;
}

// Define route access rules based on user types
const routeAccessRules: Record<string, string[]> = {
    'customer': [ROUTE.HOME, ROUTE.RESULTS],
    'oh-user': Object.values(ROUTE), 
};

/**
 * Checks if a route is accessible based on authentication state and user type
 * @param route The route to check access for
 * @returns RouteGuardResult indicating if access is allowed and redirect info if needed
 */
export function checkRouteAccess(route: { name: string; params?: RouteParams }): RouteGuardResult {
    const auth = get(isAuthenticated);
    const store = get(userStore);
    const userType = store.user?.user_type;
    const routeName = route.name.toLowerCase();

    // Always allow access to login and password reset routes
    if (routeName === ROUTE.LOGIN || routeName === ROUTE.PASSWORD_RESET) {
        return { isAllowed: true };
    }

    // If not authenticated, redirect to login
    if (!auth) {
        return {
            isAllowed: false,
            redirectTo: `/${ROUTE.LOGIN}`,
            reason: 'Authentication required'
        };
    }

    // CRITICAL: If authenticated but no user in store, clear auth state and redirect
    // This prevents the endless loop where auth=true but no user data exists
    if (auth && !store.user) {
        logWarning('Route guard: Authenticated but no user in store, clearing auth state', "RouteGuard", undefined);
        // Clear authentication state
        isAuthenticated.set(false);
        localStorage.removeItem('auth');
        localStorage.removeItem('object_id');
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenExpiration');
        localStorage.removeItem('tokenExpirationReadable');
        sessionStorage.removeItem('auth');
        userStore.clearUser();
        
        return {
            isAllowed: false,
            redirectTo: `/${ROUTE.LOGIN}`,
            reason: 'Authentication state inconsistent - cleared and redirecting'
        };
    }

    // If no user type is set, this is an error state
    if (!userType) {
        return {
            isAllowed: false,
            redirectTo: `/${ROUTE.LOGIN}`,
            reason: 'User type not set'
        };
    }

    // Check if user type has access to the route
    const allowedRoutes = routeAccessRules[userType] || [];
    if (!allowedRoutes.includes(routeName)) {
        return {
            isAllowed: false,
            redirectTo: `/${ROUTE.HOME}`,
            reason: 'Unauthorized access'
        };
    }

    return { isAllowed: true };
}

/**
 * Logs unauthorized access attempts for monitoring
 * @param route The route that was attempted to access
 * @param result The guard result containing the reason for denial
 */
export function logUnauthorizedAccess(route: RouteData, result: RouteGuardResult): void {
    const store = get(userStore);
    logWarning('Unauthorized access attempt:', "RouteGuard", undefined, {
        route: route.name,
        params: route.params,
        reason: result.reason,
        timestamp: new Date().toISOString(),
        userType: store.user?.user_type
    });
    // TODO: Add proper logging to a monitoring system
} 